import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { UserService } from '../_services/user.service';
import {
  LoadUserApplications,
  LoadUserApplicationsSuccess,
  LoadUserApplicationsFailed,
  ApplicationActionTypes,
  CreateApplication,
  CreateApplicationSuccess,
  CreateApplicationFailed,
  LoadApplication,
  LoadApplicationSuccess,
  LoadApplicationFailed,
  UpdateApplication,
  UpdateApplicationSuccess,
  UpdateApplicationFailed,
  LoadApplicationById,
  LoadApplicationByIdSuccess,
  LoadApplicationByIdFailed,
  LoadEmails,
  LoadEmailsSuccess,
  LoadEmailsFailed,
  AddEmail,
  AddEmailSuccess,
  AddEmailFailed,
  LoadIpTrackingDatatable,
  LoadIpTrackingDatatableSuccess,
  LoadIpTrackingDatatableFailed,
  GetAppMetrics,
  GetAppMetricsSuccess,
  GetAppMetricsFailed,
  LoadApplicationUsersDatatable,
  LoadApplicationUsersDatatableSuccess,
  LoadApplicationUsersDatatableFailed,
  LoadApplicationTemplates,
  LoadApplicationTemplatesSuccess,
  LoadApplicationTemplatesFailed,
  CreateApplicationTemplate,
  CreateApplicationTemplateSuccess,
  CreateApplicationTemplateFailed,
  DeleteApplicationTemplate,
  DeleteApplicationTemplateSuccess,
  DeleteApplicationTemplateFailed,
  UpdateApplicationTemplate,
  UpdateApplicationTemplateSuccess,
  UpdateApplicationTemplateFailed,
  LoadApplicationDocTemplates,
  LoadApplicationDocTemplatesSuccess,
  LoadApplicationDocTemplatesFailed,
  CreateDomain,
  CreateDomainSuccess,
  CreateDomainFailed,
  CheckDomainStatus,
  CheckDomainStatusSuccess,
  CheckDomainStatusFailed,
  LoadApplicationTemplate,
  LoadApplicationTemplateSuccess,
  LoadApplicationTemplateFailed,
  DeleteApplication,
  DeleteApplicationSuccess,
  DeleteApplicationFailed,
  GetTimeZones,
  GetTimeZonesFailed,
  GetTimeZonesSuccess,
  RenameApplicationSuccess,
  RenameApplicationFailed,
  RenameApplication,
  GetLanguages,
  GetLanguagesSuccess,
  GetLanguagesFailed,
  LoadSubdomains,
  LoadSubdomainsSuccess,
  LoadSubdomainsFailed,
} from '../_actions/application.actions';
import { CacheService } from '../../services/cache.service';
import { AvailableCaches, Dropdown } from '../_models/ignatius.models';
import { ApplicationService } from '../_services/application.service';
import {
  getApplicationIpTrackingDatatable,
  getApplicationUsersDatatable,
  getAppMetrics,
  getDocTemplates,
  getEmails,
  getTemplate,
  getTemplates,
  getUserApplications,
} from '../_selectors/application.selector';
import {
  ApplicationData,
  ApplicationTemplatePayload,
  DomainResponse,
  CheckDomainValidityResponse,
  UserApplication,
  UserEmail,
} from '../_models/application.models';
import { getApplicationData } from '../_selectors/application.selector';
import { Datatable } from '../_models/report.models';
import { RouteService } from '../../services/route.service';
import { ApplicationMetric } from '../_models/settings.models';
import { Template } from '../_models/template.model';
import { AlertService, ToastTypes } from 'src/app/shared/services/alert.service';

@Injectable()
export class ApplicationEffect {
  constructor(
    private actions: Actions,
    private service: ApplicationService,
    private store: Store,
    private cache: CacheService,
    private route: RouteService,
    private alert: AlertService,
  ) {}

  actionServiceMap:any = {
    [ApplicationActionTypes.CREATE_DOMAIN]: {
      serviceMethod: this.service.createDomain,
      successAction: CreateDomainSuccess,
      failureAction: CreateDomainFailed,
    },
    [ApplicationActionTypes.CHECK_DOMAIN_STATUS]: {
      serviceMethod: this.service.checkDomainStatus,
      successAction: CheckDomainStatusSuccess,
      failureAction: CheckDomainStatusFailed,
    },
  };

  /**
   * Get applications by user
   */
  applications: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<LoadUserApplications>(
        ApplicationActionTypes.LOAD_USER_APPLICATIONS
      ),
      withLatestFrom(this.store.pipe(select(getUserApplications))),
      mergeMap(([action, applications]) => {
        // if (
        //   applications.length &&
        //   this.cache.getCacheState(AvailableCaches.USER_APPLICATIONS)
        // )
        //   return of(new LoadUserApplicationsSuccess(applications));
        return this.service.getApplicationsbyUser(action.params).pipe(
          map(
            (payload: Array<UserApplication>) =>
              new LoadUserApplicationsSuccess(payload)
          ),
          catchError((err) => of(new LoadUserApplicationsFailed(err)))
        );
      })
    )
  );

  /**
   * Get application metric by application id
   */
  getAppMetrics: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<GetAppMetrics>(ApplicationActionTypes.GET_APP_METRICS),
      withLatestFrom(this.store.pipe(select(getAppMetrics))),
      mergeMap(([action, applications]) => {
        return this.service.getAppMetric(action.applicationId).pipe(
          map(
            (payload: ApplicationMetric) =>
              new GetAppMetricsSuccess(action.applicationId, payload)
          ),
          catchError((err) => of(new GetAppMetricsFailed(err)))
        );
      })
    )
  );

  /**
   * Create application
   */
  createApplication: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<CreateApplication>(ApplicationActionTypes.CREATE_APPLICATION),
      mergeMap((action) => {
        return this.service.createApplication(action.payload).pipe(
          map((payload: UserApplication) => {
            const instance = new CreateApplicationSuccess(payload);
            instance.refId = action.refId;
            return instance;
          }),
          catchError((err) => {
            const instance = new CreateApplicationFailed(err);
            instance.refId = action.refId;
            return of(instance);
          })
        );
      })
    )
  );

  /**
   * Update application
   */
  updateApplication: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<UpdateApplication>(ApplicationActionTypes.UPDATE_APPLICATION),
      mergeMap((action) => {
        return this.service.updateApplication(action.payload).pipe(
          map((payload: UserApplication) => {
            // const instance = new UpdateApplicationSuccess(payload);
            // this.store.dispatch(
            //   new LoadApplicationById(this.route.getApplicationId())
            // );
            let id = this.route.getApplicationKey()
              ? this.route.getApplicationKey()
              : payload.Key;
            const instance = new LoadApplicationById(id);
            return instance;
          }),
          catchError((err) => {
            const instance = new UpdateApplicationFailed(err);
            return of(instance);
          })
        );
      })
    )
  );

  /**
   * Get application
   */
  application: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<LoadApplication>(ApplicationActionTypes.LOAD_APPLICATION),
      withLatestFrom(this.store.pipe(select(getApplicationData))),
      mergeMap(([action, applicationData]) => {
        if (
          (applicationData as any)[action.tableId] &&
          this.cache.getCacheState(AvailableCaches.USER_APPLICATIONS)
        )
          return of(
            new LoadApplicationSuccess(
              action.tableId,
              (applicationData as any)[action.tableId]
            )
          );
        return this.service.getApplicationData(action.tableId).pipe(
          map(
            (payload: ApplicationData) =>
              new LoadApplicationSuccess(action.tableId, payload)
          ),
          catchError((err) => of(new LoadApplicationFailed(err, action.tableId)))
        );
      })
    )
  );

  /**
   * Get application By Id
   */
  applicationById: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<LoadApplicationById>(
        ApplicationActionTypes.LOAD_APPLICATION_BY_ID
      ),
      withLatestFrom(this.store.pipe(select(getUserApplications))),
      mergeMap(([action, applications]) => {
        const app = applications?.find((app) => app.Key === action.appId);
        if (
          applications &&
          app &&
          this.cache.getCacheState(AvailableCaches.USER_APPLICATIONS)
        ) {
          return of(new LoadApplicationByIdSuccess(action.appId, app));
        }
        return this.service
          .getApplicationById(action.appId, action.fromSettings)
          .pipe(
            map(
              (payload: UserApplication) =>
                new LoadApplicationByIdSuccess(action.appId, payload)
            ),
            catchError((err) => of(new LoadApplicationByIdFailed(err)))
          );
      })
    )
  );

  /**
   * Get emails
   */
  emails: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<LoadEmails>(ApplicationActionTypes.LOAD_EMAILS),
      withLatestFrom(this.store.pipe(select(getEmails))),
      mergeMap(([action, emails]) => {
        if (
          emails[action.applicationId] &&
          this.cache.getCacheState(AvailableCaches.USER_APPLICATIONS) &&
          !action.force
        )
          return of(
            new LoadEmailsSuccess(
              action.applicationId,
              emails[action.applicationId]
            )
          );
        return this.service
          .getEmails(action.applicationId, action.includeDefault)
          .pipe(
            map(
              (payload: Array<UserEmail>) =>
                new LoadEmailsSuccess(action.applicationId, payload)
            ),
            catchError((err) => of(new LoadEmailsFailed(err, action.applicationId)))
          );
      })
    )
  );

  /**
   * Add an Email
   */
  addEmail: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<AddEmail>(ApplicationActionTypes.ADD_EMAIL),
      mergeMap((action) => {
        return this.service.addEmail(action.payload).pipe(
          map((payload: any) => {
            const instance = new AddEmailSuccess(payload);
            instance.refId = action.refId;
            this.store.dispatch(new LoadEmails(action.appId, true));
            return instance;
          }),
          catchError((err) => {
            const instance = new AddEmailFailed(err);
            instance.refId = action.refId;
            return of(instance);
          })
        );
      })
    )
  );

  /**
   * Get IP Tracking datatable
   */
  getIPTrackingUsersDatatable: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<LoadIpTrackingDatatable>(
        ApplicationActionTypes.LOAD_IP_TRACKING_DATATABLE
      ),
      withLatestFrom(
        this.store.pipe(select(getApplicationIpTrackingDatatable))
      ),
      mergeMap(([action, datatable]) => {
        return this.service.getIPTrackingUsersDatatable(action.payload).pipe(
          map(
            (payload: Datatable) =>
              new LoadIpTrackingDatatableSuccess(
                action.payload.ApplicationKey!,
                action.payload.Start,
                action.payload.Length,
                payload
              )
          ),
          catchError((err) => of(new LoadIpTrackingDatatableFailed(err)))
        );
      })
    )
  );

  /**
   * Get application users datatable
   */
  getApplicationUsersDatatable: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<LoadApplicationUsersDatatable>(
        ApplicationActionTypes.LOAD_APPLICATIONS_USERS_DATATABLE
      ),
      withLatestFrom(this.store.pipe(select(getApplicationUsersDatatable))),
      mergeMap(([action, datatable]) => {
        return this.service.getApplicationUsersDatatable(action.payload).pipe(
          map(
            (payload: Datatable) =>
              new LoadApplicationUsersDatatableSuccess(
                action.payload.ApplicationKey!,
                action.payload.Start,
                action.payload.Length,
                payload
              )
          ),
          catchError((err) => of(new LoadApplicationUsersDatatableFailed(err)))
        );
      })
    )
  );

  /**
   * Get templates
   */
  getTemplates: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<LoadApplicationTemplates>(ApplicationActionTypes.LOAD_APPLICATION_TEMPLATES),
      withLatestFrom(this.store.pipe(select(getTemplates))),
      mergeMap(([action, templates]) => {
        // if (
        //   templates[action.appId] &&
        //   this.cache.getCacheState(AvailableCaches.TEMPLATES) &&
        //   !action.force
        // )
        //   return of(
        //     new LoadApplicationTemplatesSuccess(action.appId, templates[action.appId])
        //   );
        return this.service.getApplicationTemplates(action.appId, action.docTemplate, action.geoJson).pipe(
          map(
            (payload: Array<Template>) =>
              new LoadApplicationTemplatesSuccess(action.appId, action.docTemplate, action.geoJson, payload)
          ),
          catchError((err) => of(new LoadApplicationTemplatesFailed(err, action.appId)))
        );
      })
    )
  );

  getDocTemplates: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<LoadApplicationDocTemplates>(ApplicationActionTypes.LOAD_APPLICATION_DOC_TEMPLATES),
      withLatestFrom(this.store.pipe(select(getDocTemplates))),
      mergeMap(([action, templates]) => {
        // if (
        //   templates[action.appId] &&
        //   this.cache.getCacheState(AvailableCaches.TEMPLATES) &&
        //   !action.force
        // )
        //   return of(
        //     new LoadApplicationTemplatesSuccess(action.appId, templates[action.appId])
        //   );
        return this.service.getApplicationTemplates(action.appId, action.geoJsonTemplate ? false:true, action.geoJsonTemplate).pipe(
          map(
            (payload: Array<Template>) =>
              new LoadApplicationDocTemplatesSuccess(action.appId, payload)
          ),
          catchError((err) => of(new LoadApplicationDocTemplatesFailed(err,action.appId )))
        );
      })
    )
  );

    /**
   * Get templates
   */
    getTemplateById: Observable<Action> = createEffect(() =>
      this.actions.pipe(
        ofType<LoadApplicationTemplate>(ApplicationActionTypes.LOAD_APPLICATION_TEMPLATE),
        withLatestFrom(this.store.pipe(select(getTemplate))),
        mergeMap(([action, templates]) => {
          return this.service.getApplicationTemplateById(action.id).pipe(
            map(
              (payload: Template) =>
                new LoadApplicationTemplateSuccess(action.id, payload)
            ),
            catchError((err) => of(new LoadApplicationTemplateFailed(err)))
          );
        })
      )
    );


  /**
   * Create template
   */
  createTemplate: Observable<Action> = createEffect(() =>
      this.actions.pipe(
        ofType<CreateApplicationTemplate>(ApplicationActionTypes.CREATE_APPLICATION_TEMPLATE),
        mergeMap((action) => {
          return this.service.createTemplate(action.payload).pipe(
            map((payload: any) => {
              const instance = new CreateApplicationTemplateSuccess(payload);
              instance.refId = action.refId;
              this.store.dispatch(new LoadApplicationTemplates(action.payload.ApplicationKey));
              return instance;

            }),
            catchError((err) => {
              const instance = new CreateApplicationTemplateFailed(err);
              instance.refId = action.refId;
              return of(instance);
            })
          );
        })
      )
  );

  /**
   * Create template
   */
  updateTemplate: Observable<Action> = createEffect(() =>
      this.actions.pipe(
        ofType<UpdateApplicationTemplate>(ApplicationActionTypes.UPDATE_APPLICATION_TEMPLATE),
        mergeMap((action) => {
          return this.service.updateTemplate(action.payload).pipe(
            map((payload: any) => {
              const instance = new UpdateApplicationTemplateSuccess(payload);
              instance.refId = action.refId;
              this.store.dispatch(new LoadApplicationTemplates(action.payload.ApplicationKey));
              return instance;

            }),
            catchError((err) => {
              const instance = new UpdateApplicationTemplateFailed(err);
              instance.refId = action.refId;
              return of(instance);
            })
          );
        })
      )
  );

  /**
   * Delete template
   */
  deleteTemplate: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<DeleteApplicationTemplate>(ApplicationActionTypes.DELETE_APPLICATION_TEMPLATE),
      mergeMap((action) => {
        return this.service.deleteTemplate(action.key).pipe(
          map(() => {
            const instance = new DeleteApplicationTemplateSuccess(
              action.appId,
              action.key
            );
            instance.refId = action.refId;
            return instance;
          }),
          catchError((err) => {
            const instance = new DeleteApplicationTemplateFailed(err);
            instance.refId = action.refId;
            return of(instance);
          })
        );
      })
    )
  );

  /**
   * Effects for Domain Creation
   */
  domainEffect: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(
        ApplicationActionTypes.CREATE_DOMAIN,
        ApplicationActionTypes.CHECK_DOMAIN_STATUS
      ),
      map((action:any) => {
        const { serviceMethod, successAction, failureAction } = this.actionServiceMap[action?.type];

        serviceMethod.call(this.service, action.payload).subscribe(
          (payload:any) => {
            if (action.appId) {
              this.store.dispatch(new LoadApplicationById(action.appId));
            }
            const instance = new successAction(action.appId,payload);
            instance.refId = action.refId;
            this.store.dispatch(instance);
          },
          (err:any) => this.store.dispatch(new failureAction(err))
        );
        return { type: '[Check Domain] No Operation' };
      })
    )
  );

  /**
   * Delete Application
   */
  deleteApplication: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<DeleteApplication>(ApplicationActionTypes.DELETE_APPLICATION),
      mergeMap((action) => {
        return this.service.deleteApplication(action.appId).pipe(
          map((payload:any) => {
            const instance = new DeleteApplicationSuccess(payload);
            instance.refId = action.refId;
            return instance;
          }),
          catchError((err) => {
            const instance = new DeleteApplicationFailed(err);
            instance.refId = action.refId;
            return of(instance);
          })
        );
      })
    )
  );

    /**
   * Get Timezone Data
   */
    getTimeZoneData: Observable<Action> = createEffect(() =>
      this.actions.pipe(
        ofType<GetTimeZones>(ApplicationActionTypes.GET_TIME_ZONES),
        mergeMap((action) => {
          return this.service.getTimeZones()
            .pipe(
              map((payload: Array<any>) => {
                const instance = new GetTimeZonesSuccess(payload);
                instance.refId = action.refId;
                return instance;
              }),
              catchError((err) => {
                const instance = new GetTimeZonesFailed(err);
                instance.refId = action.refId;
                return of(instance);
              })
            );
        })
      )
    );
    /**
   * Get Languages Data
   */
    getLanguagesData: Observable<Action> = createEffect(() =>
      this.actions.pipe(
        ofType<GetLanguages>(ApplicationActionTypes.GET_LANGUAGES),
        switchMap((action) => {
          return this.service.getLanguages()
            .pipe(
              map((payload: Array<any>) => {
                const instance = new GetLanguagesSuccess(payload);
                instance.refId = action.refId;
                return instance;
              }),
              catchError((err) => {
                const instance = new GetLanguagesFailed(err);
                instance.refId = action.refId;
                return of(instance);
              })
            );
        })
      )
    );
  /**
   * Rename Application
   */
  renameApplication: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<RenameApplication>(ApplicationActionTypes.RENAME_APPLICATION),
      mergeMap((action) => {
        return this.service.renameApplication(action.payload).pipe(
          map((payload:any) => {
            const instance = new RenameApplicationSuccess(payload);
            instance.refId = action.refId;
            return instance;
          }),
          catchError((err) => {
            const instance = new RenameApplicationFailed(err);
            instance.refId = action.refId;
            return of(instance);
          })
        );
      })
    )
  );

  /**
   * Load subdomains
   */
  loadSubdomains: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<LoadSubdomains>(ApplicationActionTypes.LOAD_SUBDOMAINS),
      mergeMap(() =>
        this.service.getSubdomains().pipe(
          map((payload: any[]) => new LoadSubdomainsSuccess(payload)),
          catchError((err) => of(new LoadSubdomainsFailed(err)))
        )
      )
    )
  );
}
